import { TaskOutcomeDataSchema } from "@erp/shared-types";
import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent, TERMINAL_TASK_STATUSES } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { taskOutputSchema } from "./shared";

/**
 * Complete Task Step
 *
 * Transitions a task (or subtask) to a terminal status, recording an outcome
 * validated against the task type's outcome vocabulary. Reuses TaskService's
 * transition logic, so typed-completion gating (expected subtasks must be
 * closed) and outcome-vocabulary violations surface as step failures with
 * the service's error message.
 */
export const completeTaskStep = defineStepConfig({
  type: "complete_task",
  name: "Complete Task",
  description:
    "Transition a task to a terminal status (completed | failed | cancelled) with an outcome validated against its task type",
  category: "Tasks",

  inputSchema: z.object({
    taskId: z.uuid().describe("The task to close"),
    targetStatus: z
      .enum(TERMINAL_TASK_STATUSES)
      .optional()
      .describe(
        "Terminal status to transition to. Wire from an upstream step; overrides the config default (completed).",
      ),
    outcome: z
      .string()
      .optional()
      .describe(
        "Outcome recorded on the task (e.g. 'approved'). Validated against the task type's outcome vocabulary; required when the type declares one.",
      ),
    outcomeData: TaskOutcomeDataSchema.nullable()
      .optional()
      .describe("Supporting result data required by the task type"),
    outcomeReason: z
      .string()
      .optional()
      .describe("Free-form reason accompanying the outcome"),
    metadata: z
      .record(z.string(), z.unknown())
      .optional()
      .describe("Metadata merged into the task's existing metadata"),
  }),

  outputSchema: z.object({
    task: taskOutputSchema,
    taskId: z.uuid().describe("The closed task's id (flat, for wiring)"),
    status: z.string().describe("The terminal status the task reached"),
    outcome: z.string().nullable().describe("The recorded outcome"),
    outcomeData: TaskOutcomeDataSchema.nullable().optional(),
  }),

  configSchema: z.object({
    targetStatus: z
      .enum(TERMINAL_TASK_STATUSES)
      .optional()
      .describe(
        "Default terminal status used when the `targetStatus` input is not wired (defaults to completed).",
      ),
  }),

  requiredPermissions: [{ action: Action.Update, subject: Subject.Task }],

  // Emitted indirectly: TaskService publishes TASK_STATUS_CHANGED and
  // TASK_COMPLETED on the terminal transition (the single lifecycle emission
  // point).
  emits: [SystemEvent.TASK_COMPLETED, SystemEvent.TASK_STATUS_CHANGED] as const,
});
